import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  getPackagePersistId,
  getPackageRoomTypeId,
  getPackageStatus,
  getRoomRateLabel,
} from '~/components/Offers/Detail/OfferBuilder/Content/OfferPackages/utils';
import { isAccommodationPackage } from '~/components/Offers/Detail/typeNarrowingUtils';

import { useGetProperty } from '~/hooks/apis/accommodation/useGetProperty';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useUpdatePackage } from '~/hooks/apis/offers/useUpdatePackage';
import { useGetPackageSchema } from '~/hooks/apis/packages/useGetPackageSchema';

import { getOfferPropertyId } from '../../utils';

import LinkDealOptionsGroup from './LinkDealOptionsGroup';
import {
  type PendingLinks,
  UNASSIGNED_ROOM_TYPE_ID,
  buildRoomRateIndex,
  filterDealOptions,
  getFilterableRoomRates,
  getFilterableRoomTypes,
  getSaveResultMessage,
  groupDealOptionsByRoomType,
} from './utils';

export default function LinkDealOptionsTab() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { data: offer, isLoading: isLoadingOffer, error, refetch } = useGetOffer(offerId);
  const propertyId = getOfferPropertyId(offer);
  const { data: property, isLoading: isLoadingProperty } = useGetProperty({ propertyId });
  const roomTypes = useMemo(() => property?.room_types ?? [], [property?.room_types]);
  const updatePackageMutation = useUpdatePackage();
  const { data: packageSchema } = useGetPackageSchema(offerId);

  const [pendingLinks, setPendingLinks] = useState<PendingLinks>({});
  const [expandedRoomTypeIds, setExpandedRoomTypeIds] = useState<Array<string>>([]);
  const [expandedDealOptionIds, setExpandedDealOptionIds] = useState<Array<string>>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roomTypeFilter, setRoomTypeFilter] = useState<Array<string>>([]);
  const [roomRateFilter, setRoomRateFilter] = useState<Array<string>>([]);
  const [statusFilter, setStatusFilter] = useState('');

  // allSave is a counter, not a toggle: a boolean would un-trigger forms on every second click.
  const [allSave, setAllSave] = useState(0);
  const [allSaveTarget, setAllSaveTarget] = useState(-1);
  const [allSaveCompletedCount, setAllSaveCompletedCount] = useState(0);
  const [failedSaveLabels, setFailedSaveLabels] = useState<Array<string>>([]);
  const hasNotifiedForAllSaveRef = useRef(0);

  const dealOptions = useMemo(() => (offer?.packages ?? []).filter(isAccommodationPackage), [offer?.packages]);
  const dealOptionIndexByDealOption = useMemo(
    () => new Map<App.AccommodationPackage, number>(dealOptions.map((dealOption, index) => [dealOption, index])),
    [dealOptions],
  );

  const roomRateById = useMemo(() => buildRoomRateIndex(roomTypes), [roomTypes]);

  const hasActiveFilters = !!(searchQuery || roomTypeFilter.length || roomRateFilter.length || statusFilter);

  const filteredDealOptions = useMemo(
    () =>
      filterDealOptions(
        dealOptions,
        { searchQuery, roomTypeFilter, roomRateFilter, statusFilter },
        pendingLinks,
        roomRateById,
      ),
    [dealOptions, pendingLinks, roomRateById, roomRateFilter, roomTypeFilter, searchQuery, statusFilter],
  );

  const dealOptionsByRoomTypeId = useMemo(() => groupDealOptionsByRoomType(filteredDealOptions), [filteredDealOptions]);

  const visibleRoomTypes = useMemo(
    () => roomTypes.filter((roomType) => dealOptionsByRoomTypeId.has(roomType.id)),
    [roomTypes, dealOptionsByRoomTypeId],
  );
  const visibleRoomTypeIds = useMemo(
    () =>
      visibleRoomTypes
        .map((roomType) => roomType.id)
        .concat(dealOptionsByRoomTypeId.has(UNASSIGNED_ROOM_TYPE_ID) ? [UNASSIGNED_ROOM_TYPE_ID] : []),
    [visibleRoomTypes, dealOptionsByRoomTypeId],
  );
  const visibleDealOptionIds = useMemo(
    () => filteredDealOptions.map((dealOption) => getPackagePersistId(dealOption) ?? dealOption.name),
    [filteredDealOptions],
  );
  const areAllExpanded =
    visibleRoomTypeIds.length > 0 &&
    visibleRoomTypeIds.every((id) => expandedRoomTypeIds.includes(id)) &&
    visibleDealOptionIds.every((id) => expandedDealOptionIds.includes(id));

  const roomTypeFilterOptions = useMemo(() => getFilterableRoomTypes(dealOptions, roomTypes), [dealOptions, roomTypes]);
  const hasDealOptionsWithNoRoom = useMemo(
    () => dealOptions.some((dealOption) => !getPackageRoomTypeId(dealOption)),
    [dealOptions],
  );
  const roomRateOptions = useMemo(() => getFilterableRoomRates(dealOptions, roomTypes), [dealOptions, roomTypes]);

  const isLoading = isLoadingOffer || isLoadingProperty;
  const hasPendingLinks = Object.keys(pendingLinks).length > 0;

  const saveAllButtonState: 'default' | 'saving' | 'saved' | 'failed' =
    allSaveTarget === -1
      ? 'default'
      : allSaveCompletedCount < allSaveTarget
        ? 'saving'
        : failedSaveLabels.length
          ? 'failed'
          : 'saved';

  function handleSetRoomRateForAll(roomTypeDealOptions: Array<App.AccommodationPackage>, roomRateId: string) {
    setPendingLinks((current) => {
      const next = { ...current };
      roomTypeDealOptions.forEach((dealOption) => {
        const packageId = getPackagePersistId(dealOption);
        if (packageId) {
          next[packageId] = roomRateId;
        }
      });
      return next;
    });
  }

  function handleToggleExpandAll() {
    const isExpanding = !areAllExpanded;
    setExpandedRoomTypeIds(isExpanding ? visibleRoomTypeIds : []);
    setExpandedDealOptionIds(isExpanding ? visibleDealOptionIds : []);
  }

  function handleClearFilters() {
    setSearchQuery('');
    setRoomTypeFilter([]);
    setRoomRateFilter([]);
    setStatusFilter('');
  }

  async function handleSaveAll() {
    if (!offerId) {
      return;
    }

    const linkFailureLabels: Array<string> = [];
    if (hasPendingLinks) {
      const results = await Promise.allSettled(
        Object.entries(pendingLinks).map(([packageId, roomRateId]) =>
          updatePackageMutation.mutateAsync({
            offerId,
            packageId,
            packageData: { fk_room_rate_id: roomRateId },
          }),
        ),
      );
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const [packageId] = Object.entries(pendingLinks)[index];
          linkFailureLabels.push(`${packageId}: ${(result.reason as Error)?.message ?? 'Could not save link'}`);
        }
      });
    }

    setFailedSaveLabels(linkFailureLabels);
    setAllSaveCompletedCount(0);
    setAllSaveTarget(filteredDealOptions.length);
    setAllSave((current) => current + 1);
  }

  function handlePackageSaveComplete() {
    setAllSaveCompletedCount((current) => (current < allSaveTarget ? current + 1 : current));
  }
  function handlePackageSaveFailed(dealOptionLabel: string) {
    setAllSaveCompletedCount((current) => (current < allSaveTarget ? current + 1 : current));
    setFailedSaveLabels((current) => current.concat(dealOptionLabel));
  }

  useEffect(() => {
    if (allSaveTarget === -1 || allSaveCompletedCount < allSaveTarget || hasNotifiedForAllSaveRef.current === allSave) {
      return;
    }
    hasNotifiedForAllSaveRef.current = allSave;
    refetch().then(() => setPendingLinks({}));
    const failedCount = failedSaveLabels.length;
    const savedCount = allSaveCompletedCount - failedCount;
    const { message, variant } = getSaveResultMessage(savedCount, failedCount);
    enqueueSnackbar(message, { variant });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSaveCompletedCount, allSaveTarget]);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography variant="h6">Link deal options</Typography>
        <Typography variant="body2" color="text.secondary">
          Map each deal option to a room rate, package regions and brand overrides. Changes are held until you save.
        </Typography>
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={areAllExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
          disabled={visibleRoomTypeIds.length === 0}
          onClick={handleToggleExpandAll}
        >
          {areAllExpanded ? 'Collapse all options' : 'Expand all options'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={filteredDealOptions.length === 0 || saveAllButtonState === 'saving'}
          onClick={handleSaveAll}
        >
          {saveAllButtonState === 'saving'
            ? `Saving... ${allSaveCompletedCount}/${allSaveTarget}`
            : saveAllButtonState === 'saved'
              ? 'All Saved'
              : 'Save All'}
        </Button>
      </Stack>
      {saveAllButtonState === 'failed' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {failedSaveLabels.length === 1
            ? 'This deal option failed to save:'
            : `These ${failedSaveLabels.length} deal options failed to save:`}
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
            {failedSaveLabels.map((label, index) => (
              <li key={`${label}-${index}`}>{label}</li>
            ))}
          </Box>
        </Alert>
      )}

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" flexWrap="wrap" useFlexGap gap={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Search deal options..."
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setExpandedRoomTypeIds([]);
              }}
              sx={{ minWidth: 260 }}
            />
            <TextField
              select
              size="small"
              label="Room"
              value={roomTypeFilter}
              onChange={(event) => {
                const value = event.target.value;
                setRoomTypeFilter(typeof value === 'string' ? value.split(',') : value);
                setExpandedRoomTypeIds([]);
              }}
              slotProps={{
                select: {
                  multiple: true,
                  displayEmpty: true,
                  renderValue: (selected: Array<string>) =>
                    selected.length ? `${selected.length} room${selected.length === 1 ? '' : 's'}` : 'All rooms',
                },
                inputLabel: { shrink: true },
              }}
              sx={{ minWidth: 200 }}
            >
              {roomTypeFilterOptions.map((roomType) => (
                <MenuItem key={roomType.id} value={roomType.id}>
                  <Checkbox checked={roomTypeFilter.includes(roomType.id)} size="small" />
                  <ListItemText primary={roomType.name} />
                </MenuItem>
              ))}
              {hasDealOptionsWithNoRoom && (
                <MenuItem value={UNASSIGNED_ROOM_TYPE_ID}>
                  <Checkbox checked={roomTypeFilter.includes(UNASSIGNED_ROOM_TYPE_ID)} size="small" />
                  <ListItemText primary="Not linked to a room" />
                </MenuItem>
              )}
            </TextField>
            <TextField
              select
              size="small"
              label="Rate plan"
              value={roomRateFilter}
              onChange={(event) => {
                const value = event.target.value;
                setRoomRateFilter(typeof value === 'string' ? value.split(',') : value);
                setExpandedRoomTypeIds([]);
              }}
              slotProps={{
                select: {
                  multiple: true,
                  displayEmpty: true,
                  renderValue: (selected: Array<string>) =>
                    selected.length
                      ? `${selected.length} rate plan${selected.length === 1 ? '' : 's'}`
                      : 'All rate plans',
                },
                inputLabel: { shrink: true },
              }}
              sx={{ minWidth: 200 }}
            >
              {roomRateOptions.map((roomRate) => {
                const label = getRoomRateLabel(roomRate) || roomRate.id;
                return (
                  <MenuItem key={roomRate.id} value={label}>
                    <Checkbox checked={roomRateFilter.includes(label)} size="small" />
                    <ListItemText primary={label} />
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setExpandedRoomTypeIds([]);
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="content-approved">
                {getPackageStatus({ status: 'content-approved' } as App.Package).label}
              </MenuItem>
              <MenuItem value="draft">{getPackageStatus({ status: 'draft' } as App.Package).label}</MenuItem>
              <MenuItem value="hidden">{getPackageStatus({ status: 'hidden' } as App.Package).label}</MenuItem>
            </TextField>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              <strong>{filteredDealOptions.length}</strong> deal option{filteredDealOptions.length === 1 ? '' : 's'} in{' '}
              <strong>{dealOptionsByRoomTypeId.size}</strong> room{dealOptionsByRoomTypeId.size === 1 ? '' : 's'}
            </Typography>
            {hasActiveFilters && (
              <Button size="small" startIcon={<FilterListOffIcon />} onClick={handleClearFilters}>
                Clear filters
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {isLoading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : error ? (
        <Alert severity="error">Unable to load deal options.</Alert>
      ) : dealOptions.length === 0 ? (
        <Alert severity="info">This offer has no deal options yet.</Alert>
      ) : filteredDealOptions.length === 0 ? (
        <Alert severity="info">No deal options match the current filters.</Alert>
      ) : (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
            {visibleRoomTypes
              .concat(
                dealOptionsByRoomTypeId.has(UNASSIGNED_ROOM_TYPE_ID)
                  ? [{ id: UNASSIGNED_ROOM_TYPE_ID, name: 'Not linked to a room type', room_rates: [] }]
                  : [],
              )
              .map((roomType) => (
                <LinkDealOptionsGroup
                  key={roomType.id}
                  roomType={roomType}
                  dealOptions={dealOptionsByRoomTypeId.get(roomType.id) ?? []}
                  getPackageIndex={(dealOption) => dealOptionIndexByDealOption.get(dealOption) ?? -1}
                  pendingLinks={pendingLinks}
                  expanded={expandedRoomTypeIds.includes(roomType.id)}
                  onExpandedChange={(isExpanded) =>
                    setExpandedRoomTypeIds((current) =>
                      isExpanded ? current.concat(roomType.id) : current.filter((id) => id !== roomType.id),
                    )
                  }
                  expandedDealOptionIds={expandedDealOptionIds}
                  onDealOptionExpandedChange={(dealOptionId, isExpanded) =>
                    setExpandedDealOptionIds((current) =>
                      isExpanded ? current.concat(dealOptionId) : current.filter((id) => id !== dealOptionId),
                    )
                  }
                  onSetRoomRateForAll={
                    roomType.id === UNASSIGNED_ROOM_TYPE_ID
                      ? () => {}
                      : (roomRateId) =>
                          handleSetRoomRateForAll(dealOptionsByRoomTypeId.get(roomType.id) ?? [], roomRateId)
                  }
                  offer={offer}
                  property={property}
                  packageSchema={packageSchema}
                  onPackageSaved={handlePackageSaveComplete}
                  onPackageSaveFailed={handlePackageSaveFailed}
                  allSave={allSave}
                />
              ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
